import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';

// ─── Colour palette (16 pastel tones) ────────────────────────────────────────

export const TAG_COLORS: { key: string; label: string; bg: string; text: string; border: string }[] = [
  { key: 'blue',     label: 'Azul',      bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  { key: 'sky',      label: 'Celeste',   bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' },
  { key: 'teal',     label: 'Teal',      bg: '#ccfbf1', text: '#0f766e', border: '#5eead4' },
  { key: 'green',    label: 'Verde',     bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  { key: 'lime',     label: 'Lima',      bg: '#ecfccb', text: '#4d7c0f', border: '#bef264' },
  { key: 'yellow',   label: 'Amarelo',   bg: '#fef9c3', text: '#a16207', border: '#fde047' },
  { key: 'amber',    label: 'Âmbar',     bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
  { key: 'orange',   label: 'Laranja',   bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  { key: 'red',      label: 'Vermelho',  bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  { key: 'rose',     label: 'Rosa',      bg: '#ffe4e6', text: '#be123c', border: '#fda4af' },
  { key: 'pink',     label: 'Pink',      bg: '#fce7f3', text: '#be185d', border: '#f9a8d4' },
  { key: 'purple',   label: 'Roxo',      bg: '#f3e8ff', text: '#7e22ce', border: '#d8b4fe' },
  { key: 'lavender', label: 'Lavanda',   bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
  { key: 'brown',    label: 'Marrom',    bg: '#f5f0eb', text: '#7c5c41', border: '#c8a98a' },
  { key: 'gray',     label: 'Cinza',     bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
  { key: 'slate',    label: 'Slate',     bg: '#e2e8f0', text: '#334155', border: '#94a3b8' },
];

export function getTagColor(key: string) {
  return TAG_COLORS.find(c => c.key === key) ?? TAG_COLORS[0];
}

// Auto-assign next colour for a new tag based on how many tags exist
export function nextAutoColor(existingCount: number) {
  return TAG_COLORS[existingCount % TAG_COLORS.length].key;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Tag {
  id: number;
  nome: string;
  cor: string;
}

interface Props {
  selectedTags: Tag[];
  onChange: (tags: Tag[]) => void;
  onTagCreated?: (tag: Tag) => void;
  disabled?: boolean;
}

// ─── Styled components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span<{ $bg: string; $text: string; $border: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${p => p.$bg};
  color: ${p => p.$text};
  border: 1.5px solid ${p => p.$border};
  white-space: nowrap;
`;

const ChipRemove = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: inherit;
  opacity: 0.6;
  line-height: 1;
  display: flex;
  align-items: center;
  &:hover { opacity: 1; }
  svg { width: 12px; height: 12px; }
`;

const InputRow = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus { border-color: var(--accent, #3b82f6); }
  &:disabled { background: #f8fafc; cursor: not-allowed; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 500;
  max-height: 220px;
  overflow-y: auto;
`;

const DropdownItem = styled.button.attrs({ type: 'button' })<{ $isCreate?: boolean }>`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 13px;
  color: ${p => p.$isCreate ? 'var(--accent, #3b82f6)' : '#1e293b'};
  font-weight: ${p => p.$isCreate ? 600 : 400};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.1s;
  &:hover { background: #f1f5f9; }
`;

const ColorDot = styled.span<{ $bg: string; $border: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.$bg};
  border: 1.5px solid ${p => p.$border};
  flex-shrink: 0;
`;

const ColorPickerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 14px;
  border-top: 1px solid #f1f5f9;
`;

const ColorSwatch = styled.button.attrs({ type: 'button' })<{ $bg: string; $border: string; $selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${p => p.$bg};
  border: 2px solid ${p => p.$selected ? '#1e293b' : p.$border};
  cursor: pointer;
  transition: transform 0.1s;
  &:hover { transform: scale(1.2); }
`;

const EmptyNote = styled.div`
  padding: 10px 14px;
  font-size: 13px;
  color: #94a3b8;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const TagSelector: React.FC<Props> = ({ selectedTags, onChange, onTagCreated, disabled }) => {
  const { session } = useAuth();
  const { can } = usePermissionCheck();
  const canManage = can('tags.manage');

  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [open, setOpen] = useState(false);
  const [pendingColor, setPendingColor] = useState<string>('blue');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch group tags on mount
  useEffect(() => {
    if (!session?.access_token) return;
    apiFetch(`${API_BASE_URL}/api/v1/tags`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(r => r.json())
      .then(d => setAllTags(d.data || []))
      .catch(() => {});
  }, [session]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedIds = new Set(selectedTags.map(t => t.id));

  const filtered = allTags.filter(
    t => !selectedIds.has(t.id) && t.nome.toLowerCase().includes(inputVal.toLowerCase())
  );

  const trimmed = inputVal.trim();
  const exactExists = allTags.some(t => t.nome.toLowerCase() === trimmed.toLowerCase());
  const showCreate = canManage && trimmed.length > 0 && !exactExists;

  const selectTag = (tag: Tag) => {
    onChange([...selectedTags, tag]);
    setInputVal('');
    setOpen(false);
    setShowColorPicker(false);
  };

  const removeTag = (id: number) => {
    onChange(selectedTags.filter(t => t.id !== id));
  };

  const createTag = async (colorOverride?: string) => {
    if (!session?.access_token || !trimmed || creating) return;
    const color = colorOverride ?? pendingColor ?? nextAutoColor(allTags.length);
    setCreating(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/tags`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nome: trimmed, cor: color }),
      });
      const data = await res.json();
      if (res.ok && data.data) {
        setAllTags(prev => [...prev, data.data].sort((a, b) => a.nome.localeCompare(b.nome)));
        onTagCreated?.(data.data);
        selectTag(data.data);
        setPendingColor(nextAutoColor(allTags.length + 1));
      }
    } catch {}
    setCreating(false);
  };

  return (
    <Wrapper ref={wrapperRef}>
      {/* Selected chips */}
      {selectedTags.length > 0 && (
        <ChipsRow>
          {selectedTags.map(tag => {
            const c = getTagColor(tag.cor);
            return (
              <Chip key={tag.id} $bg={c.bg} $text={c.text} $border={c.border}>
                {tag.nome}
                {!disabled && (
                  <ChipRemove onClick={() => removeTag(tag.id)} title="Remover tag">
                    <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </ChipRemove>
                )}
              </Chip>
            );
          })}
        </ChipsRow>
      )}

      {/* Input */}
      {!disabled && (
        <InputRow>
          <SearchInput
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => { if (e.key === 'Enter' && showCreate && !creating) { e.preventDefault(); createTag(); } }}
            placeholder="Buscar ou criar tag..."
            disabled={creating}
          />

          {open && (
            <Dropdown>
              {filtered.map(tag => {
                const c = getTagColor(tag.cor);
                return (
                  <DropdownItem key={tag.id} onClick={() => selectTag(tag)}>
                    <ColorDot $bg={c.bg} $border={c.border} />
                    {tag.nome}
                  </DropdownItem>
                );
              })}

              {filtered.length === 0 && !showCreate && (
                <EmptyNote>
                  {trimmed ? 'Nenhuma tag encontrada' : 'Nenhuma tag disponível'}
                </EmptyNote>
              )}

              {showCreate && (
                <>
                  <DropdownItem $isCreate onClick={() => { setShowColorPicker(p => !p); }} disabled={creating}>
                    {creating
                      ? <Spinner />
                      : <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                          <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    }
                    {creating ? 'Criando…' : `Criar "${trimmed}"`}
                    {!creating && (
                      <ColorDot
                        $bg={getTagColor(pendingColor).bg}
                        $border={getTagColor(pendingColor).border}
                        style={{ marginLeft: 'auto' }}
                      />
                    )}
                  </DropdownItem>

                  {showColorPicker && !creating && (
                    <ColorPickerRow>
                      {TAG_COLORS.map(c => (
                        <ColorSwatch
                          key={c.key}
                          $bg={c.bg}
                          $border={c.border}
                          $selected={pendingColor === c.key}
                          title={c.label}
                          onClick={() => { setPendingColor(c.key); setShowColorPicker(false); createTag(c.key); }}
                        />
                      ))}
                    </ColorPickerRow>
                  )}

                  {!showColorPicker && !creating && (
                    <DropdownItem onClick={() => createTag()} style={{ paddingTop: 4, paddingBottom: 8, color: '#64748b', fontSize: 12 }}>
                      Enter para criar com cor atual · Clique em "Criar" para escolher cor
                    </DropdownItem>
                  )}
                </>
              )}
            </Dropdown>
          )}
        </InputRow>
      )}
    </Wrapper>
  );
};

export default TagSelector;
